import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import React, { createContext, ReactNode, useContext, useState } from 'react';

type Params = {
  title: string;
  message: string | JSX.Element;
  confirmButtonText: string;
  abortButtonText?: string;
};

interface ConfirmationContextType {
  showConfirmation: (props: Params) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(
  undefined
);

interface ConfirmationProviderProps {
  children: ReactNode;
}

export const ConfirmationProvider: React.FC<ConfirmationProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<
    | (Params & {
        resolve: (_: boolean) => void;
      })
    | null
  >(null);

  const showConfirmation = (props: Params) => {
    return new Promise<boolean>(resolve => {
      setDialogProps({ ...props, resolve });
      setOpen(true);
    });
  };

  const handleClose = (confirmed: boolean) => {
    setOpen(false);
    if (dialogProps?.resolve) {
      dialogProps.resolve(confirmed);
    }
    setDialogProps(null);
  };

  return (
    <ConfirmationContext.Provider value={{ showConfirmation }}>
      {children}
      {dialogProps && (
        <Dialog open={open} onClose={() => handleClose(false)}>
          <DialogTitle>{dialogProps.title}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogProps.message}</DialogContentText>
          </DialogContent>
          <DialogActions>
            {dialogProps.abortButtonText && (
              <Button onClick={() => handleClose(false)}>
                {dialogProps.abortButtonText}
              </Button>
            )}
            <Button onClick={() => handleClose(true)} autoFocus>
              {dialogProps.confirmButtonText}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error(
      'useConfirmation must be used within a ConfirmationProvider'
    );
  }
  return context;
};
